'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Search, X, Users, Clock, Globe, Music, Landmark, MapPin, Feather, Layers, Star, ChevronRight, Loader2, FolderOpen, Link2, Sparkles, FileText, AlertCircle, PenLine } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

type ProjectCategory =
  | 'family' | 'clan' | 'tribe' | 'custom' | 'culture' |'music' | 'art' | 'event' | 'institution' | 'location' | 'myth';

interface DocProject {
  id: string;
  title: string;
  description: string;
  category: ProjectCategory;
  region: string | null;
  era_id: string | null;
  status: 'active' | 'completed' | 'archived';
  contributor_count: number;
  story_count: number;
  created_at: string;
  creator_name: string | null;
  creator_id: string;
  tags: string[];
  linked_timelines: string[];
}

interface NewProjectForm {
  title: string;
  description: string;
  category: ProjectCategory;
  region: string;
  era_id: string;
  tags: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_META: Record<ProjectCategory, { label: string; icon: React.ElementType; color: string; bg: string }> = {
  family:      { label: 'Family',      icon: Users,    color: '#E05A47', bg: '#E05A4714' },
  clan:        { label: 'Clan',        icon: Users,    color: '#7B6FA3', bg: '#7B6FA314' },
  tribe:       { label: 'Tribe',       icon: Globe,    color: '#2C5E3B', bg: '#2C5E3B14' },
  custom:      { label: 'Custom',      icon: Feather,  color: '#F39C12', bg: '#F39C1214' },
  culture:     { label: 'Culture',     icon: Layers,   color: '#008080', bg: '#00808014' },
  music:       { label: 'Music',       icon: Music,    color: '#E05A47', bg: '#E05A4714' },
  art:         { label: 'Art',         icon: Star,     color: '#F39C12', bg: '#F39C1214' },
  event:       { label: 'Event',       icon: Clock,    color: '#2C5E3B', bg: '#2C5E3B14' },
  institution: { label: 'Institution', icon: Landmark, color: '#7B6FA3', bg: '#7B6FA314' },
  location:    { label: 'Location',    icon: MapPin,   color: '#008080', bg: '#00808014' },
  myth:        { label: 'Myth',        icon: Sparkles, color: '#E05A47', bg: '#E05A4714' },
};

const ERA_LABELS: Record<string, string> = {
  'era-1': 'Early Iron Age',
  'era-2': 'Kingdom of Mutapa',
  'era-3': 'Rozvi Empire',
  'era-4': 'Colonial Contact',
  'era-5': 'Anti Colonial Resistance',
  'era-6': 'Nationalist Period',
  'era-7': 'Post-Independence',
};

const CATEGORIES = Object.keys(CATEGORY_META) as ProjectCategory[];

// ─── Project Card ─────────────────────────────────────────────────────────────

function ProjectCard({ project, onJoin }: { project: DocProject; onJoin: (id: string) => void }) {
  const meta = CATEGORY_META[project.category];
  const Icon = meta.icon;
  const [date, setDate] = useState('');
  useEffect(() => {
    setDate(new Date(project.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }));
  }, [project.created_at]);

  return (
    <div
      className="group rounded-xl border overflow-hidden transition-all duration-200 hover:-translate-y-0.5 flex flex-col"
      style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = meta.color + '66';
        (e.currentTarget as HTMLDivElement).style.boxShadow = `0 4px 20px ${meta.color}14`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
      }}
    >
      {/* Top accent */}
      <div className="h-1 w-full" style={{ backgroundColor: meta.color, opacity: 0.7 }} />

      <div className="p-5 flex flex-col flex-1 gap-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
            style={{ backgroundColor: meta.bg, color: meta.color }}
          >
            <Icon size={12} />
            {meta.label}
          </div>
          {project.era_id && ERA_LABELS[project.era_id] && (
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>
              {ERA_LABELS[project.era_id]}
            </span>
          )}
        </div>

        {/* Title & Description */}
        <div>
          <h3 className="text-sm font-bold leading-snug mb-1.5" style={{ color: 'var(--foreground)' }}>
            {project.title}
          </h3>
          <p className="text-xs leading-relaxed line-clamp-3" style={{ color: 'var(--muted-foreground)' }}>
            {project.description}
          </p>
        </div>

        {/* Tags */}
        {project.tags && project.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {project.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="text-xs px-2 py-0.5 rounded-full"
                style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}
              >
                #{tag}
              </span>
            ))}
            {project.tags.length > 3 && (
              <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>+{project.tags.length - 3}</span>
            )}
          </div>
        )}

        {/* Linked timelines */}
        {project.linked_timelines && project.linked_timelines.length > 0 && (
          <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--primary)' }}>
            <Link2 size={11} />
            <span>{project.linked_timelines.length} timeline{project.linked_timelines.length !== 1 ? 's' : ''} linked</span>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t mt-auto" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <span className="flex items-center gap-1">
              <Users size={11} />
              {project.contributor_count}
            </span>
            <span className="flex items-center gap-1">
              <FileText size={11} />
              {project.story_count} stories
            </span>
            {project.region && (
              <span className="flex items-center gap-1">
                <MapPin size={11} />
                {project.region}
              </span>
            )}
          </div>
          <button
            onClick={() => onJoin(project.id)}
            className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full transition-all active:scale-95"
            style={{ backgroundColor: meta.bg, color: meta.color }}
          >
            Contribute
            <ChevronRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── New Project Modal ─────────────────────────────────────────────────────────

function NewProjectModal({
  onClose,
  onCreated,
  userId,
}: {
  onClose: () => void;
  onCreated: () => void;
  userId: string;
}) {
  const [form, setForm] = useState<NewProjectForm>({
    title: '',
    description: '',
    category: 'family',
    region: '',
    era_id: '',
    tags: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      setError('Title and description are required.');
      return;
    }
    setSaving(true);
    setError(null);
    const supabase = createClient();
    const tags = form.tags.split(',').map((t) => t.trim()).filter(Boolean);
    const { error: dbError } = await supabase.from('doc_projects').insert({
      title: form.title.trim(),
      description: form.description.trim(),
      category: form.category,
      region: form.region.trim() || null,
      era_id: form.era_id || null,
      creator_id: userId,
      tags,
      status: 'active',
      linked_timelines: [],
    });
    setSaving(false);
    if (dbError) {
      setError(dbError.message);
      return;
    }
    onCreated();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div
        className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
          <div>
            <h2 className="text-base font-bold" style={{ color: 'var(--foreground)' }}>Start a Documentation Project</h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
              Preserve a family, clan, tribe, custom, culture, music, art, event, institution, location, or myth
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full transition-colors"
            style={{ color: 'var(--muted-foreground)' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--muted)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm" style={{ backgroundColor: '#E05A4714', color: '#E05A47' }}>
              <AlertCircle size={15} />
              {error}
            </div>
          )}

          {/* Category */}
          <div>
            <label className="block text-xs font-semibold mb-2" style={{ color: 'var(--foreground)' }}>Category *</label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {CATEGORIES.map((cat) => {
                const m = CATEGORY_META[cat];
                const CatIcon = m.icon;
                const isSelected = form.category === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, category: cat }))}
                    className="flex flex-col items-center gap-1 p-2 rounded-lg border text-xs font-medium transition-all"
                    style={{
                      borderColor: isSelected ? m.color : 'var(--border)',
                      backgroundColor: isSelected ? m.bg : 'transparent',
                      color: isSelected ? m.color : 'var(--muted-foreground)',
                    }}
                  >
                    <CatIcon size={14} />
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>Project Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder={`e.g. The Ndlovu Family of Matabeleland`}
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border transition-colors"
              style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--primary)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>Description *</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="What are you documenting? Why does it matter? What sources or knowledge do you have?"
              rows={4}
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border transition-colors resize-none"
              style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--primary)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
            />
          </div>

          {/* Region & Era */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>Region</label>
              <input
                type="text"
                value={form.region}
                onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
                placeholder="e.g. Matabeleland"
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border transition-colors"
                style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--primary)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>Historical Era</label>
              <select
                value={form.era_id}
                onChange={(e) => setForm((f) => ({ ...f, era_id: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border transition-colors"
                style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
              >
                <option value="">Select era…</option>
                {Object.entries(ERA_LABELS).map(([id, label]) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>Tags</label>
            <input
              type="text"
              value={form.tags}
              onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
              placeholder="oral-tradition, Shona, pre-colonial (comma-separated)"
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none border transition-colors"
              style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--primary)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
            />
            <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
              Separate tags with commas. These help connect your project to existing timelines.
            </p>
          </div>

          {/* Timeline connection note */}
          <div
            className="flex items-start gap-2.5 p-3 rounded-lg"
            style={{ backgroundColor: '#E05A4708', border: '1px solid #E05A4722' }}
          >
            <Link2 size={14} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 1 }} />
            <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
              Once your project is created, you can link it to existing timelines on the platform. Contributors can then attach their stories to your project and the connected timelines.
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ color: 'var(--muted-foreground)' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--muted)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all active:scale-95 disabled:opacity-50"
            style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? 'Creating…' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function DocProjectsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<DocProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ProjectCategory | ''>('');
  const [showNewModal, setShowNewModal] = useState(false);

  const loadProjects = async () => {
    setLoading(true);
    const supabase = createClient();
    const { data, error } = await supabase
      .from('doc_projects')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      const creatorIds = [...new Set(data.map((p: any) => p.creator_id).filter(Boolean))];
      let nameMap: Record<string, string> = {};
      if (creatorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, full_name')
          .in('id', creatorIds);
        for (const p of profiles || []) {
          nameMap[p.id] = p.full_name || '';
        }
      }
      setProjects(
        data.map((p: any) => ({
          ...p,
          creator_name: nameMap[p.creator_id] || null,
          tags: p.tags || [],
          linked_timelines: p.linked_timelines || [],
          contributor_count: p.contributor_count || 0,
          story_count: p.story_count || 0,
        }))
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const handleJoin = (projectId: string) => {
    if (!user) {
      window.location.href = '/sign-up-login';
      return;
    }
    window.location.href = `/contribution-submission?project=${projectId}`;
  };

  const filtered = projects.filter((p) => {
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.description.toLowerCase().includes(search.toLowerCase());
    const matchCat = !categoryFilter || p.category === categoryFilter;
    return matchSearch && matchCat;
  });

  return (
    <AppLayout>
      <div className="pt-16 min-h-screen" style={{ backgroundColor: 'var(--background)' }}>
        {/* Hero */}
        <div
          className="border-b relative overflow-hidden"
          style={{ borderColor: 'var(--border)', background: 'linear-gradient(135deg, #E05A4708 0%, #2C5E3B06 100%)' }}
        >
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-12">
            <div className="max-w-2xl">
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium mb-6"
                style={{ borderColor: '#E05A4744', backgroundColor: '#E05A470D', color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}
              >
                <FolderOpen size={12} />
                Community Documentation Projects
              </div>
              <h1 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: 'var(--foreground)' }}>
                Document what hasn't been documented yet.
              </h1>
              <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--muted-foreground)' }}>
                Start a project to preserve the history of a family, clan, tribe, custom, culture, music tradition, artwork, event, institution, location, or myth — and connect your stories to established timelines on the platform.
              </p>
              {user && (
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => setShowNewModal(true)}
                    className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all active:scale-95 shadow-sm border"
                    style={{ backgroundColor: 'var(--card)', borderColor: 'var(--primary)', color: 'var(--primary)' }}
                  >
                    <Plus size={16} />
                    Start a Documentation Project
                  </button>
                  <Link
                    href="/contribution-submission"
                    className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all active:scale-95 shadow-sm"
                    style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                  >
                    <PenLine size={16} />
                    Add a Story
                  </Link>
                </div>
              )}
              {!user && (
                <Link
                  href="/sign-up-login"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all active:scale-95 shadow-sm"
                  style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  <Plus size={16} />
                  Sign in to start a project
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="border-b sticky top-16 z-30" style={{ borderColor: 'var(--border)', backgroundColor: 'rgba(250,247,242,0.95)', backdropFilter: 'blur(12px)' }}>
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-3 flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-full flex-1 min-w-0 max-w-xs" style={{ backgroundColor: 'var(--muted)', border: '1px solid var(--border)' }}>
              <Search size={14} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search projects…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 bg-transparent text-sm outline-none min-w-0"
                style={{ color: 'var(--foreground)' }}
              />
              {search && (
                <button onClick={() => setSearch('')} style={{ color: 'var(--muted-foreground)' }}>
                  <X size={13} />
                </button>
              )}
            </div>

            {/* Category filters */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5">
              <button
                onClick={() => setCategoryFilter('')}
                className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
                style={{
                  backgroundColor: !categoryFilter ? 'var(--primary)' : 'var(--muted)',
                  color: !categoryFilter ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
                }}
              >
                All
              </button>
              {CATEGORIES.map((cat) => {
                const m = CATEGORY_META[cat];
                const CatIcon = m.icon;
                const isActive = categoryFilter === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setCategoryFilter(isActive ? '' : cat)}
                    className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
                    style={{
                      backgroundColor: isActive ? m.bg : 'var(--muted)',
                      color: isActive ? m.color : 'var(--muted-foreground)',
                      border: isActive ? `1px solid ${m.color}44` : '1px solid transparent',
                    }}
                  >
                    <CatIcon size={11} />
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Projects Grid */}
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-8">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={28} className="animate-spin" style={{ color: 'var(--primary)' }} />
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-center gap-4">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: '#E05A4714' }}
              >
                <FolderOpen size={28} style={{ color: 'var(--primary)' }} />
              </div>
              <div>
                <p className="text-lg font-bold mb-2" style={{ color: 'var(--foreground)' }}>
                  {search || categoryFilter ? 'No projects match your filters' : 'No documentation projects yet'}
                </p>
                <p className="text-sm max-w-sm" style={{ color: 'var(--muted-foreground)' }}>
                  {search || categoryFilter
                    ? 'Try adjusting your search or filters.' :'Be the first to start a documentation project and preserve a piece of African history.'}
                </p>
              </div>
              {!search && !categoryFilter && user && (
                <button
                  onClick={() => setShowNewModal(true)}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-95"
                  style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  <Plus size={15} />
                  Start the first project
                </button>
              )}
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  {filtered.length} project{filtered.length !== 1 ? 's' : ''}
                  {categoryFilter && ` in ${CATEGORY_META[categoryFilter].label}`}
                </p>
                {user && (
                  <div className="flex items-center gap-2">
                    <Link
                      href="/contribution-submission"
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all active:scale-95 border"
                      style={{ backgroundColor: 'var(--card)', borderColor: 'var(--primary)', color: 'var(--primary)' }}
                    >
                      <PenLine size={13} />
                      Add a Story
                    </Link>
                    <button
                      onClick={() => setShowNewModal(true)}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all active:scale-95"
                      style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                    >
                      <Plus size={13} />
                      New Project
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered.map((project) => (
                  <ProjectCard key={project.id} project={project} onJoin={handleJoin} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {showNewModal && user && (
        <NewProjectModal
          userId={user.id}
          onClose={() => setShowNewModal(false)}
          onCreated={loadProjects}
        />
      )}
    </AppLayout>
  );
}
